import React from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../../components/annuaire/designSystem';
import ConnexionModule, {
  CompletePhoneContent,
  PLACEHOLDER_PHONE,
} from '../../components/ConnexionModule';
import { formatSupplement } from './bookingRequest';
import { lienConfidentialiteAtelier } from './confidentialiteAtelier';
import LigneBonusRepar from '../../components/annuaire/shared/LigneBonusRepar';
import { horaireCourt, horaireLong } from './creneaux';
import {
  BoutonVert,
  Corps,
  Encart,
  Icone,
  PetitTexte,
  Pied,
  SousTitre,
  Titre,
  icone,
} from './ui';

/**
 * Étape 3, le compte (forme 1 validée le 21/09 : les raisons d'abord, puis le
 * formulaire du site). Le compte est obligatoire : c'est lui qui donne son
 * `client_id` à la demande, et donc la confirmation par email (FIXERLIGHT-014),
 * l'origine RJ et la suite (FIXERLIGHT-052).
 *
 * Le formulaire est `ConnexionModule`, celui du site, monté ici en ligne et non
 * en modale. `stayOnPage` le fait se refermer sur place ; son bouton dit
 * « … et envoyer », et le tunnel envoie dès que le compte existe.
 */

const Raisons = styled.div`
  border: 1.5px solid ${colors.borderLight};
  border-radius: 16px;
  padding: 14px 14px 4px;
  margin-bottom: 18px;

  h3 {
    margin: 0 0 10px;
    font-family: ${fonts.heading};
    font-size: 15px;
    color: ${colors.textDark};
  }
`;

const Raison = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 10px;
  font-size: 13.5px;
  line-height: 1.4;

  b {
    display: block;
    font-weight: 600;
  }
  span span {
    color: ${colors.textSecondary};
  }
`;

const Recap = styled.dl`
  margin: 0 0 18px;
  padding: 4px 14px;
  border-radius: 16px;
  background: ${colors.grisClair};

  div {
    display: flex;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid #e4e8ec;
    font-size: 13.5px;
    line-height: 1.4;
  }
  div:last-child {
    border-bottom: none;
  }
  dt {
    flex: 0 0 110px;
    font-weight: 600;
    color: ${colors.textSecondary};
  }
  dd {
    margin: 0;
  }
`;

const Compte = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 14px;
  margin-bottom: 18px;
  border-radius: 16px;
  background: ${colors.vertPastel};
  font-size: 14px;
  line-height: 1.4;

  b {
    font-weight: 600;
  }
`;

/** Le formulaire du site, ramené à l'échelle du tunnel (son titre est fait pour
 * une page entière). */
const Formulaire = styled.div`
  h1,
  h2,
  h3 {
    font-size: 18px !important;
    line-height: 1.2 !important;
    margin-bottom: 8px !important;
  }
`;

const LienPolitique = styled.a`
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${colors.textDark};
  }
  &:focus-visible {
    outline: 3px solid ${colors.textDark};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

/** FIXERLIGHT-095 : à qui partent les coordonnées. Une ligne, pas un geste. */
const MentionTransmission = ({ atelier }) => (
  <PetitTexte>
    Vos coordonnées sont transmises à {atelier} pour votre rendez-vous.{' '}
    <LienPolitique
      href={lienConfidentialiteAtelier()}
      target="_blank"
      rel="noopener noreferrer">
      En savoir plus
    </LienPolitique>
  </PetitTexte>
);

const telValide = (client) =>
  !!(client && client.tel && client.tel !== PLACEHOLDER_PHONE);

export default function EtapeCompte({
  atelier,
  client,
  instant,
  adresse,
  probleme,
  couverture,
  bonus = null,
  selected,
  envoiEnCours,
  erreur,
  onConnecte,
  onEnvoyer,
  titreRef,
}) {
  const connecte = !!(client && client.id);
  const cta =
    instant && selected[0]
      ? `Réserver ${horaireCourt(selected[0])}`
      : 'Envoyer ma demande';
  const ctaEnCours = instant ? 'Réservation en cours…' : 'Envoi en cours…';
  // Le supplément déjà vu à l'étape 1 (FIXERLIGHT-066), redit ici : le
  // récapitulatif est la dernière chose lue avant l'envoi.
  const supplement =
    couverture && couverture.covered && couverture.surcharge_eur
      ? formatSupplement(couverture.surcharge_eur)
      : null;

  const recap = (
    <Recap>
      <div>
        <dt>Adresse</dt>
        <dd>{adresse}</dd>
      </div>
      {supplement && (
        <div>
          <dt>Supplément</dt>
          <dd>
            <b>{supplement}</b> de déplacement
          </dd>
        </div>
      )}
      <div>
        <dt>Votre problème</dt>
        <dd>{probleme}</dd>
      </div>
      <div>
        <dt>{instant ? 'Horaire' : 'Créneaux'}</dt>
        <dd>
          {instant
            ? horaireLong(selected[0])
            : `${selected.length} créneau${
                selected.length > 1 ? 'x' : ''
              } proposé${selected.length > 1 ? 's' : ''}`}
        </dd>
      </div>
      <LigneBonusRepar paliers={bonus} />
    </Recap>
  );

  if (!connecte) {
    return (
      <Corps>
        <Titre tabIndex={-1} ref={titreRef}>
          Dernière étape : votre compte
        </Titre>
        <SousTitre>
          {instant ? 'Pour réserver' : 'Pour envoyer votre demande'}, il faut un
          compte Roulez Jeunesse. Il est gratuit.
        </SousTitre>
        <Raisons>
          <h3>Ce que votre compte vous apporte</h3>
          <Raison>
            <Icone src={icone('MSG_MAIL')} alt="" $taille={24} />
            <span>
              <b>
                {instant
                  ? 'Votre confirmation par email'
                  : 'Le créneau retenu, par email'}
              </b>
              <span>
                {instant
                  ? 'Instantanément, avec l’adresse et l’heure.'
                  : 'Dès que l’atelier a choisi.'}
              </span>
            </span>
          </Raison>
          <Raison>
            <Icone src={icone('CALENDRIER')} alt="" $taille={24} />
            <span>
              <b>Vos rendez-vous au même endroit</b>
              <span>Les retrouver, les suivre, les déplacer.</span>
            </span>
          </Raison>
          <Raison>
            <Icone src={icone('USER')} alt="" $taille={24} />
            <span>
              <b>Rien à ressaisir la prochaine fois</b>
              <span>Chez cet atelier comme chez les autres.</span>
            </span>
          </Raison>
        </Raisons>
        {erreur && (
          <Encart $ton="alerte" role="alert">
            {erreur}
          </Encart>
        )}
        {/* Avant le formulaire : son bouton crée le compte ET envoie la demande,
            la ligne doit donc se lire avant ce geste, pas sous lui. */}
        <MentionTransmission atelier={atelier} />
        <Formulaire>
          <ConnexionModule
            noPadding
            bg="white"
            stayOnPage
            titleSignup="Créez votre compte"
            titleSignin="Connectez-vous"
            ctaLabelSignup={
              instant
                ? 'Créer mon compte et réserver'
                : 'Créer mon compte et envoyer'
            }
            ctaLabelSignin={
              instant ? 'Me connecter et réserver' : 'Me connecter et envoyer'
            }
            closeModal={onConnecte}
          />
        </Formulaire>
      </Corps>
    );
  }

  const nom = [client.prenom, client.nom].filter(Boolean).join(' ');

  return (
    <>
      <Corps>
        <Titre tabIndex={-1} ref={titreRef}>
          {instant ? 'Vérifiez et réservez' : 'Vérifiez et envoyez'}
        </Titre>
        {erreur && (
          <Encart $ton="alerte" role="alert">
            {erreur}
          </Encart>
        )}
        <Compte>
          <span>
            <b>{nom || client.email}</b>
            <br />
            {[telValide(client) ? client.tel : null, client.email]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </Compte>
        {!telValide(client) && (
          <>
            <SousTitre>
              Ajoutez votre téléphone : l’atelier vous appelle s’il a besoin
              d’une précision.
            </SousTitre>
            <CompletePhoneContent client={client} />
          </>
        )}
        {recap}
        <MentionTransmission atelier={atelier} />
      </Corps>
      <Pied>
        <BoutonVert
          type="button"
          disabled={envoiEnCours || !telValide(client)}
          onClick={onEnvoyer}>
          {envoiEnCours ? ctaEnCours : cta}
        </BoutonVert>
      </Pied>
    </>
  );
}
